<template>
  <header
    class="mobile-header"
    :class="{
      'mobile-header--vecura': isVecura,
      'mobile-header--bar-hidden': isHeaderHidden,
    }"
  >
    <div
      class="mobile-header__bar"
      :class="{ 'mobile-header__bar--hidden': isHeaderHidden }"
    >
      <a class="mobile-header__brand" href="/" aria-label="Startseite">
        <BrandLogo
          :show-name="false"
          :variant="isVecura ? 'vecura' : 'default'"
        />
        <span>Timo Rothdeutsch</span>
      </a>
    </div>

    <nav
      class="mobile-header__rail"
      v-if="showSectionRail"
      aria-label="Abschnitte"
    >
      <a
        v-for="item in sectionItems"
        :key="item.href"
        :class="{
          'mobile-header__rail-link--active': isSectionActive(item.id),
        }"
        class="mobile-header__rail-link"
        :href="item.href"
        :aria-label="item.label"
      />
    </nav>

    <nav class="mobile-header__dock-wrap" aria-label="Mobile Hauptnavigation">
      <div
        class="mobile-header__dock"
        :class="{ 'mobile-header__dock--hidden': isDockHidden }"
      >
        <a
          v-for="item in primaryNavItems"
          :key="item.href"
          :class="{
            'mobile-header__dock-link--active': isActive(item.href),
          }"
          class="mobile-header__dock-link"
          :href="item.href"
          :aria-label="item.label"
        >
          <MobileSymbolIcon :type="item.icon" />
          <span class="mobile-header__dock-label">{{ item.label }}</span>
        </a>
        <a
          class="mobile-header__dock-link"
          :class="{ 'mobile-header__dock-link--active': isMoreActive }"
          href="/mehr"
          aria-label="Info"
        >
          <MobileSymbolIcon type="info" />
          <span class="mobile-header__dock-label">Info</span>
        </a>
      </div>
    </nav>
  </header>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import BrandLogo from "@/components/BrandLogo.vue";
import MobileSymbolIcon from "@/components/icons/MobileSymbolIcon.vue";

type MobileNavIcon = "profile" | "vecura";
type ScrollDirection = "up" | "down";

const primaryNavItems: Array<{
  label: string;
  href: string;
  icon: MobileNavIcon;
}> = [
  {
    label: "Start",
    href: "/",
    icon: "profile",
  },
  {
    label: "Vecura",
    href: "/vecura",
    icon: "vecura",
  },
];

const sectionItems = [
  {
    id: "profile",
    label: "Profil",
    href: "/",
  },
  {
    id: "expertise",
    label: "Expertise",
    href: "/#expertise",
  },
  {
    id: "contact",
    label: "Kontakt",
    href: "/#contact",
  },
];

export default defineComponent({
  name: "MobileHeader",
  components: {
    BrandLogo,
    MobileSymbolIcon,
  },
  props: {
    activeSection: {
      type: String,
      required: true,
    },
    currentPath: {
      type: String,
      required: true,
    },
    isVecura: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isDockHidden = ref(false);
    const isHeaderHidden = ref(false);
    let lastScrollY = 0;
    let lastScrollDirection: ScrollDirection | null = null;
    let scrollIntentDistance = 0;

    const normalizedPath = computed(() => {
      const path = props.currentPath.split("#")[0].replace(/\/+$/, "");
      return path || "/";
    });
    const showSectionRail = computed(
      () => !props.isVecura && normalizedPath.value === "/"
    );
    const isMoreActive = computed(
      () => props.activeSection === "more" || props.activeSection === "legal"
    );

    function syncDockVisibility(): void {
      const nextScrollY = window.scrollY;
      const delta = nextScrollY - lastScrollY;
      const scrollBottom = nextScrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const isAtPageEnd = scrollBottom >= documentHeight - 12;

      if (nextScrollY < 120 || isAtPageEnd) {
        isHeaderHidden.value = false;
        isDockHidden.value = false;
        resetScrollIntent();
      } else if (delta > 0) {
        trackScrollIntent("down", delta);

        if (scrollIntentDistance > 40 && nextScrollY > 150) {
          isHeaderHidden.value = true;
        }

        if (scrollIntentDistance > 72 && nextScrollY > 220) {
          isDockHidden.value = true;
        }
      } else if (delta < 0) {
        trackScrollIntent("up", Math.abs(delta));

        if (scrollIntentDistance > 16) {
          isHeaderHidden.value = false;
        }

        if (scrollIntentDistance > 24) {
          isDockHidden.value = false;
        }
      }

      lastScrollY = Math.max(nextScrollY, 0);
    }

    // Accumulate scroll intent so the dock reacts to deliberate movement,
    // not to tiny browser scroll corrections.
    function trackScrollIntent(
      direction: ScrollDirection,
      distance: number
    ): void {
      if (lastScrollDirection !== direction) {
        lastScrollDirection = direction;
        scrollIntentDistance = 0;
      }

      scrollIntentDistance += distance;
    }

    function resetScrollIntent(): void {
      lastScrollDirection = null;
      scrollIntentDistance = 0;
    }

    function isActive(href: string): boolean {
      if (href === "/") {
        return (
          !props.isVecura &&
          ["profile", "expertise", "contact"].includes(props.activeSection)
        );
      }

      if (href === "/vecura") {
        return props.isVecura || props.activeSection === "vecura";
      }

      if (href === "/impressum") {
        return props.activeSection === "legal";
      }

      return props.currentPath.split("#")[0].replace(/\/+$/, "") === href;
    }

    function isSectionActive(sectionId: string): boolean {
      return props.activeSection === sectionId;
    }

    onMounted(() => {
      lastScrollY = window.scrollY;
      window.addEventListener("scroll", syncDockVisibility, {
        passive: true,
      });
    });

    onBeforeUnmount(() => {
      window.removeEventListener("scroll", syncDockVisibility);
    });

    watch(
      () => props.currentPath,
      () => {
        isHeaderHidden.value = false;
        isDockHidden.value = false;
        resetScrollIntent();
        lastScrollY = window.scrollY;
      }
    );

    return {
      isActive,
      isDockHidden,
      isHeaderHidden,
      isMoreActive,
      isSectionActive,
      primaryNavItems,
      sectionItems,
      showSectionRail,
    };
  },
});
</script>

<style scoped>
.mobile-header {
  position: sticky;
  z-index: 60;
  top: 0;
  display: none;
  border-bottom: 1px solid rgba(20, 20, 20, 0.08);
  background: transparent;
  transition: border-color 320ms ease;
}

.mobile-header--bar-hidden {
  border-bottom-color: transparent;
}

.mobile-header__bar {
  display: flex;
  align-items: center;
  overflow: hidden;
  max-height: 78px;
  min-height: 62px;
  padding: 8px 16px;
  background: rgba(244, 241, 234, 0.9);
  backdrop-filter: blur(18px);
  transition: max-height 420ms cubic-bezier(0.22, 1, 0.36, 1),
    min-height 420ms cubic-bezier(0.22, 1, 0.36, 1), opacity 360ms ease,
    padding 420ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 420ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: max-height, opacity, transform;
}

.mobile-header__bar--hidden {
  max-height: 0;
  min-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
  pointer-events: none;
  transform: translateY(-12px);
}

.mobile-header__brand {
  display: inline-flex;
  gap: 11px;
  align-items: center;
  min-width: 0;
  color: var(--color-ink);
  font-size: 0.96rem;
  font-weight: 900;
  text-decoration: none;
}

.mobile-header__brand :deep(.brand-logo__asset) {
  width: 44px;
}

.mobile-header__brand span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-header__dock-wrap {
  position: fixed;
  z-index: 70;
  right: 50%;
  bottom: 14px;
  bottom: calc(14px + env(safe-area-inset-bottom, 0px));
  left: auto;
  width: max-content;
  transform: translateX(50%);
}

.mobile-header__dock {
  display: grid;
  grid-template-columns: repeat(3, 42px);
  gap: 4px;
  width: max-content;
  border: 1px solid rgba(20, 20, 20, 0.1);
  border-radius: 999px;
  background: rgba(251, 248, 240, 0.94);
  box-shadow: 0 18px 50px rgba(31, 28, 20, 0.18);
  padding: 5px;
  backdrop-filter: blur(20px);
  transition: opacity 520ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}

.mobile-header__dock--hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(calc(100% + 34px));
}

.mobile-header__dock-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--color-muted);
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  transition: background 180ms ease, color 180ms ease, box-shadow 180ms ease,
    transform 180ms ease;
}

.mobile-header__dock-link svg {
  width: 19px;
  height: 19px;
}

.mobile-header__dock-link .mobile-symbol-svg--info {
  width: 22px;
  height: 22px;
}

.mobile-header__dock-link :deep(.mobile-symbol-icon--profile) {
  transition: filter 180ms ease, transform 180ms ease;
}

.mobile-header__dock-label {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.mobile-header__dock-link--active {
  background: var(--color-ink);
  color: #ffffff;
  box-shadow: 0 10px 24px rgba(20, 20, 20, 0.18);
}

.mobile-header__dock-link--active :deep(.mobile-symbol-icon--profile) {
  filter: brightness(0) invert(1);
}

.mobile-header__dock-link:active {
  transform: scale(0.97);
}

.mobile-header__rail {
  position: fixed;
  z-index: 64;
  top: 50%;
  right: 4px;
  display: grid;
  gap: 2px;
  border: 1px solid rgba(20, 20, 20, 0.06);
  border-radius: 999px;
  background: rgba(251, 248, 240, 0.32);
  padding: 6px 2px;
  transform: translateY(-50%);
  backdrop-filter: blur(12px);
}

.mobile-header__rail-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 24px;
  border-radius: 999px;
  background: transparent;
}

.mobile-header__rail-link::after {
  width: 3px;
  height: 14px;
  border-radius: 999px;
  background: rgba(20, 20, 20, 0.2);
  content: "";
  transition: background 180ms ease, height 180ms ease, opacity 180ms ease;
}

.mobile-header__rail-link--active::after {
  height: 28px;
  background: var(--color-ink);
}

.mobile-header--vecura {
  border-bottom-color: rgba(229, 230, 231, 0.12);
}

.mobile-header--vecura .mobile-header__bar {
  background: rgba(14, 21, 28, 0.9);
}

.mobile-header--vecura .mobile-header__brand {
  color: #f4f7fa;
}

.mobile-header--vecura .mobile-header__dock {
  border-color: rgba(229, 230, 231, 0.16);
  background: rgba(14, 21, 28, 0.92);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.34);
}

.mobile-header--vecura .mobile-header__dock-link {
  color: rgba(229, 230, 231, 0.66);
}

.mobile-header--vecura :deep(.mobile-symbol-icon--profile) {
  background-image: url("../assets/rothdeutsch-mark-vecura.png");
}

.mobile-header--vecura .mobile-header__dock-link--active {
  background: rgba(0, 181, 237, 0.2);
  color: #ffffff;
}

.mobile-header--vecura .mobile-header__rail {
  display: none;
}

@media (max-width: 760px) {
  .mobile-header {
    display: block;
  }
}
</style>
